/**
 * The Permit workspace (/designs/<id>/permit, docs/features/permit-plan-sets.md "Dashboard"): a full-screen page
 * next to the editor (Design | Permit, Shift+P) with a stepper that never locks (Equipment › Site › Structure ›
 * Wiring › Review & generate), each step's status (Done, N problems, Not started · optional) derived from the free
 * preview's checks and notes, and a persistent checks panel in two groups (Problems, then Passed, collapsed) whose
 * problems locate their step and element and offer a fix where one exists. Problems never block generation. Notes
 * (what could not be checked, or only informs) show on their step. On a phone the stepper scrolls, the checks are a
 * bottom sheet and photo capture comes first.
 *
 * The workspace computes everything from the design's current saved version. Everything the installer does is saved
 * on the server as they go, whether or not they pay: the site details, the SLD edits and the draft (request inputs
 * and the step) autosave (debounced; one "Saving…" / "All changes saved" indicator in the top bar), save at once when
 * the workspace is left in the app (a route change, "Buy Lux") and through a keepalive request when the page
 * unloads (tab close, reload, Stripe Checkout); photos upload at once. Opening the workspace restores all of it,
 * at the step the installer was on. "Buy Lux" comes back here: checkout returns to the Review step
 * (`?step=review&checkout=paid`), which offers to generate the plan set with the Lux just bought.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLuxSpend } from "../components/LuxSpend";
import { activityHref } from "../lib/luxSpend";
import { LuxGlyph } from "../components/LuxIcon";
import { AlertTriangle, ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, Circle, ClipboardCheck, Crosshair, Info, Keyboard, ListChecks, Loader2, PanelRightClose, PanelRightOpen, Wrench, X, XCircle, Map as MapIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError, api, errorMessage, sendKeepalive, unwrap, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { useLuxWallet } from "../api/queries";
import { spendableLux } from "../lib/luxSpend";
import { combineSaveStates } from "../lib/autosave";
import { checkoutResult, draftBody, initialStep, inputsOf, permitPath, purchaseArrived, purchasePrompt, type PurchasePrompt } from "../lib/permitDraft";
import { Alert, Badge, Button, Spinner, cx, useToast } from "../components/ui";
import { HelpLink } from "../components/HelpLink";
import { CheatSheet } from "../editor/CheatSheet";
import { workspaceForAction } from "../editor/workspace.ts";
import { useWorkspaceSwitch, WorkspaceSwitch } from "../components/WorkspaceSwitch";
import { formatCombo, isMac, isTypingTarget, loadOverrides, matchAction, resolveBindings, saveOverrides, type Overrides as KeyOverrides } from "../editor/hotkeys.ts";
import { shownVersion } from "../lib/format";
import { problemsText, sortChecks, type CheckStatus } from "../lib/permits";
import {
  GROUPS,
  STEPS,
  attachmentFix,
  luxText,
  formatLength,
  groupChecks,
  locateCheck,
  nextProblem,
  problemIds,
  stepBadge,
  stepStatuses,
  uncheckedStatuses,
  type FaceResult,
  type StepId,
  type StepStatus,
  type Units,
  type WorkStep,
} from "../lib/permitWorkspace";
import { normalize, reduceOverrides, signature, type Overrides } from "../lib/sldOverrides";
import { requestBody, useAutosave, useEditorDoc, usePermitDraft, usePermitSite, usePlanSets, usePreview, useRefreshPreview, useSldState, type Inputs, type SaveState } from "./data";
import { EquipmentStep } from "./EquipmentStep";
import { ReviewStep } from "./ReviewStep";
import { SiteStep } from "./SiteStep";
import { StructureStep } from "./StructureStep";
import { WiringStep } from "./WiringStep";
import { FormattedMessage } from "react-intl";
import { useT, type Translate } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import { useFormat } from "./format";
import "./workspace.css";

export type Check = S["PlanSetCheck"] & { status: CheckStatus };
export type SiteDetails = S["PermitSiteDetails"];

export interface Focus {
  anchor: string;
  nonce: number;
}

/** What every step gets. */
export interface Ws {
  designId: string;
  version: number;
  doc: S["EditorDocument"];
  units: Units;
  canEdit: boolean;
  phone: boolean;
  inputs: Inputs;
  setInputs: (patch: Partial<Inputs>) => void;
  body: S["PlanSetInputs"];
  preview: ReturnType<typeof usePreview>;
  checks: Check[];
  /** What could not be checked or only informs (design temperatures and their source, optional inputs left out). */
  notes: Check[];
  statuses: Record<StepId, StepStatus>;
  site: S["PermitSite"] | undefined;
  details: SiteDetails;
  setDetails: (update: (d: SiteDetails) => SiteDetails) => void;
  siteSave: SaveState;
  refreshSite: () => void;
  sld: S["SldOverridesState"] | undefined;
  overrides: Overrides;
  dispatch: (a: Parameters<typeof reduceOverrides>[1]) => void;
  sldSave: SaveState;
  rendered: Overrides;
  planSets: ReturnType<typeof usePlanSets>;
  focus: Focus | null;
  selected: string | null;
  setSelected: (id: string | null) => void;
  locate: (checkId: string) => void;
  focusAnchor: (step: WorkStep, anchor: string) => void;
  fixFor: (check: Check) => { label: string; apply: () => void } | null;
  /** The Lux the company can spend on a plan set (without the Lux reserved for automatic designs). */
  credits: number | undefined;
  goStep: (s: StepId) => void;
  /** Saves every pending change now; resolves when the server has them (or a save failed). */
  flushAll: () => Promise<unknown>;
  /** Saves every pending change before leaving for billing; rejects when one could not be saved (the Lux popover
   * then stays with a Retry). */
  flushBeforeLeave: () => Promise<unknown>;
  /** Where "Buy Lux" comes back to: this workspace's Review step. */
  returnTo: string;
  /** After a checkout return: "You now have N Lux, generate the plan set?" */
  purchase: PurchasePrompt | null;
  dismissPurchase: () => void;
  generateRef: React.MutableRefObject<(() => void) | null>;
  sheetNav: React.MutableRefObject<((dir: 1 | -1) => void) | null>;
  sldKeys: React.MutableRefObject<{ rename: () => void; reset: () => void } | null>;
}

function useMedia(query: string): boolean {
  const [match, setMatch] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setMatch(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [query]);
  return match;
}

const STEP_ICON: Record<StepStatus["state"], ReactNode> = {
  problem: <XCircle size={15} className="pw-ic fail" />,
  notstarted: <Circle size={15} className="pw-ic muted" />,
  done: <CheckCircle2 size={15} className="pw-ic ok" />,
  unchecked: <Circle size={15} className="pw-ic muted" />,
};

const CHECK_ICON: Record<string, ReactNode> = {
  gap: <AlertTriangle size={15} className="pw-ic fail" />,
  problem: <XCircle size={15} className="pw-ic fail" />,
  pass: <CheckCircle2 size={15} className="pw-ic ok" />,
};

const EMPTY_DETAILS: SiteDetails = {};

export function PermitWorkspace() {
  const { designId = "" } = useParams();
  const doc = useEditorDoc(designId);
  const draft = usePermitDraft(designId);
  const navigate = useNavigate();
  const t = useT();
  if (doc.isLoading || draft.isLoading) return <div className="pw-shell pw-center"><Spinner label={t("permit.workspace.opening")} /></div>;
  if (doc.error || !doc.data || draft.error || !draft.data)
    return (
      <div className="pw-shell pw-center">
        <div className="stack" style={{ maxWidth: 460 }}>
          <Alert tone="danger" title={t("permit.workspace.openFailed")}>{errorMessage(doc.error ?? draft.error)}</Alert>
          <div className="row">
            <Button onClick={() => navigate(`/designs/${designId}`)}>{t("common.back")}</Button>
            {draft.error && !doc.error && <Button variant="ghost" onClick={() => draft.refetch()}>{t("common.retry")}</Button>}
          </div>
        </div>
      </div>
    );
  if (!doc.data.panels.length || !doc.data.roof_model) {
    // what is missing, and where to add it (a Sketch is drawn in the Sketch tools)
    const sketch = doc.data.mode === "sketch";
    const noRoof = !doc.data.roof_model;
    return (
      <div className="pw-shell pw-center">
        <div className="pw-empty card">
          <ClipboardCheck size={34} />
          <h2>{sketch ? (noRoof ? t("permit.workspace.empty.drawRoof") : t("permit.workspace.empty.placePanels")) : t("permit.workspace.empty.addPanels")}</h2>
          <p className="muted">
            {sketch
              ? noRoof
                ? t("permit.workspace.empty.noRoofBody")
                : t("permit.workspace.empty.noPanelsBody")
              : t("permit.workspace.empty.designerBody")}
          </p>
          <div className="row" style={{ justifyContent: "center" }}>
            <Button variant="primary" onClick={() => navigate(`/designs/${designId}/${sketch ? "sketch" : "edit"}`)}>{sketch ? t("permit.workspace.empty.openSketch") : t("permit.workspace.empty.openEditor")}</Button>
            <Button variant="ghost" onClick={() => navigate(`/designs/${designId}`)}>{t("permit.workspace.backToDesign")}</Button>
          </div>
        </div>
      </div>
    );
  }
  return <Workspace key={`${designId}:${doc.data.version_number}`} doc={doc.data} draft={draft.data} />;
}

function Workspace({ doc, draft }: { doc: S["EditorDocument"]; draft: S["PermitDraftState"] }) {
  const designId = doc.design_id;
  const version = doc.version_number;
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const f = useFormat();
  const org = useOrg();
  const orgId = useOrgId();
  const canEdit = org.can("designs_leads", "edit");
  const canSeeCredits = org.can("credits_billing", "view");
  const wallet = useLuxWallet(orgId);
  const phone = useMedia("(max-width: 760px)");
  const mac = useMemo(() => isMac(), []);
  const [params, setParams] = useSearchParams();
  const stepParam = params.get("step");
  // the URL's step (a link, the checkout return), else the one the installer was on
  const step: StepId = initialStep(stepParam, draft.step);
  const goStep = useCallback(
    (s: StepId) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          next.set("step", s);
          next.delete("check");
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  // ------------------------------------------------------------------ inputs, site details, SLD edits
  const [inputs, setInputsState] = useState<Inputs>(() => inputsOf(draft.inputs as Record<string, unknown>));
  const setInputs = useCallback((patch: Partial<Inputs>) => setInputsState((v) => ({ ...v, ...patch })), []);
  const body = useMemo(() => requestBody(inputs), [inputs]);
  // the draft (request inputs and step) autosaves like the site details: nothing lives only in this browser
  const draftValue = useMemo(() => draftBody(inputs, step), [inputs, step]);
  const draftSaved = useMemo(() => JSON.stringify(draftBody(inputsOf(draft.inputs as Record<string, unknown>), initialStep(null, draft.step))), [draft]);
  const draftSave = useAutosave(
    canEdit ? draftValue : null,
    (d) => JSON.stringify(d),
    async (d) => {
      const saved = await unwrap(api.PUT("/v1/designs/{design_id}/permit-draft", { params: { path: { design_id: designId } }, body: d as S["PermitDraft"] }));
      qc.setQueryData(["permit-draft", designId], saved);
    },
    400,
    (d) => sendKeepalive("PUT", `/v1/designs/${designId}/permit-draft`, d),
    draftSaved,
  );
  const refreshPreview = useRefreshPreview(designId);
  const site = usePermitSite(designId, version);
  const [details, setDetailsState] = useState<SiteDetails | null>(null);
  useEffect(() => {
    if (site.data && details == null) setDetailsState(site.data.details);
  }, [site.data, details]);
  const setDetails = useCallback((update: (d: SiteDetails) => SiteDetails) => setDetailsState((d) => update(d ?? EMPTY_DETAILS)), []);
  const siteSave = useAutosave(
    details,
    (d) => JSON.stringify(d),
    async (d) => {
      const saved = await unwrap(api.PUT("/v1/designs/{design_id}/permit-site", { params: { path: { design_id: designId } }, body: d }));
      qc.setQueryData(["permit-site", designId, version], (old: S["PermitSite"] | undefined) => (old ? { ...old, ...saved, faces: old.faces, orphaned_faces: old.orphaned_faces } : saved));
      refreshPreview();
    },
    700,
    (d) => sendKeepalive("PUT", `/v1/designs/${designId}/permit-site`, d),
  );
  const refreshSite = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["permit-site", designId] });
    refreshPreview();
  }, [qc, designId, refreshPreview]);

  const sld = useSldState(designId, version);
  const [overrides, dispatchRaw] = useReducer(reduceOverrides, null, () => normalize(null));
  const [sldReady, setSldReady] = useState(false);
  useEffect(() => {
    if (sld.data && !sldReady) {
      dispatchRaw({ type: "replace", overrides: normalize(sld.data.overrides) });
      setSldReady(true);
    }
  }, [sld.data, sldReady]);
  // which edits each preview was drawn with: the drawing is moved on screen by what it still lacks
  const [sldRev, setSldRev] = useState(0);
  const savedByRev = useRef(new Map<number, Overrides>());
  const sldSave = useAutosave(
    sldReady ? overrides : null,
    signature,
    async (o) => {
      const state = await unwrap(api.PUT("/v1/designs/{design_id}/sld-overrides", { params: { path: { design_id: designId }, query: { version_number: version } }, body: o }));
      qc.setQueryData(["sld-overrides", designId, version], state);
      setSldRev((r) => {
        savedByRev.current.set(r + 1, o);
        return r + 1;
      });
      qc.invalidateQueries({ queryKey: ["plan-set-preview", designId], refetchType: "none" }); // the summaries elsewhere
    },
    500,
    (o) => sendKeepalive("PUT", `/v1/designs/${designId}/sld-overrides?version_number=${version}`, o),
  );
  const saveState = combineSaveStates([draftSave.state, siteSave.state, sldSave.state]);
  const flushAll = useCallback(
    () => Promise.all([draftSave.flush(), siteSave.flush(), sldSave.flush()]),
    [draftSave, siteSave, sldSave],
  );
  const flushBeforeLeave = useCallback(
    () => Promise.all([draftSave.flushOrThrow(), siteSave.flushOrThrow(), sldSave.flushOrThrow()]),
    [draftSave, siteSave, sldSave],
  );
  if (sldReady && !savedByRev.current.has(0)) savedByRev.current.set(0, normalize(sld.data?.overrides));

  const preview = usePreview(designId, version, body, null, sldRev);
  const rendered = savedByRev.current.get(preview.data?.rev ?? 0) ?? normalize(sld.data?.overrides);
  const planSets = usePlanSets(designId);

  // ------------------------------------------------------------------ back from checkout (Buy Lux)
  const [purchase, setPurchase] = useState(() => checkoutResult(params.get("checkout")));
  const [purchaseTimedOut, setPurchaseTimedOut] = useState(false);
  const spendable = canSeeCredits ? spendableLux(wallet.data, false) ?? undefined : undefined;
  const planCost = preview.data?.cost_lux ?? planSets.data?.cost_lux ?? null;
  const arrived = purchase === "paid" && (purchaseTimedOut || (planCost != null && purchaseArrived(null, spendable, planCost)));
  useEffect(() => {
    if (!purchase) return;
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        next.delete("checkout");
        next.delete("payment");
        next.set("step", "review");
        return next;
      },
      { replace: true },
    );
    if (purchase === "canceled") {
      toast(t("permit.workspace.toast.checkoutCancelled"));
      setPurchase(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // the purchase reaches the wallet with the payment's confirmation (a webhook, usually seconds)
    if (purchase !== "paid" || arrived) return;
    const started = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - started > 30_000) setPurchaseTimedOut(true);
      else void qc.invalidateQueries({ queryKey: ["lux", orgId] });
    }, 2000);
    return () => window.clearInterval(id);
  }, [purchase, arrived, qc, orgId]);
  const purchasePromptValue = purchase === "paid" ? purchasePrompt(t, spendable, planCost ?? 0, arrived) : null;

  const checks = useMemo(() => (preview.data ? sortChecks(preview.data.electrical.checks as Check[]) : []), [preview.data]);
  const notes = useMemo(() => (preview.data?.electrical.notes ?? []) as Check[], [preview.data]);
  const statuses = useMemo(() => (preview.data ? stepStatuses(checks, notes) : uncheckedStatuses(preview.error)), [preview.data, preview.error, checks, notes]);

  // ------------------------------------------------------------------ locate, select, fix
  const [panelOpen, setPanelOpen] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [focus, setFocus] = useState<Focus | null>(null);
  const [selected, setSelected] = useState<string | null>(params.get("check"));
  const focusAnchor = useCallback(
    (s: WorkStep, anchor: string) => {
      if (s !== step) goStep(s);
      setFocus({ anchor, nonce: Date.now() });
    },
    [step, goStep],
  );
  const locate = useCallback(
    (id: string) => {
      setSelected(id);
      const target = locateCheck(id);
      focusAnchor(target.step, target.anchor);
      if (phone) setSheetOpen(false);
    },
    [focusAnchor, phone],
  );
  // a ?check= link locates its check once the checks are in
  const linked = useRef(params.get("check"));
  useEffect(() => {
    if (linked.current && checks.some((c) => c.id === linked.current)) {
      locate(linked.current);
      linked.current = null;
    }
  }, [checks, locate]);

  const faces = (preview.data?.electrical.structural?.faces ?? []) as FaceResult[];
  const units: Units = site.data?.units ?? (preview.data?.electrical.structural?.units as Units | undefined) ?? "imperial";
  const fixFor = useCallback(
    (c: Check) => {
      if (!canEdit) return null;
      const faceMatch = /^structural_(.+)_spacing$/.exec(c.id);
      if (faceMatch && c.status === "problem") {
        const face = faces.find((f) => f.face_id === faceMatch[1]);
        const fix = face ? attachmentFix(face) : null;
        if (!face || fix == null) return null;
        const length = formatLength(fix, units, f.digits);
        return {
          label: t("permit.workspace.fix.spacing", { length }),
          apply: () => {
            setDetails((d) => ({ ...d, faces: { ...(d.faces ?? {}), [face.face_id]: { ...(d.faces?.[face.face_id] ?? {}), attachment_spacing_m: fix } } }));
            toast(t("permit.workspace.toast.spacingSet", { face: face.label, length }));
          },
        };
      }
      return null;
    },
    [faces, units, canEdit, setDetails, toast, t, f],
  );

  // ------------------------------------------------------------------ Design | Sketch | Permit (the workspace autosaves: flush, then switch)
  const workspace = useWorkspaceSwitch({ designId, current: "permit", state: { dirty: false, readOnly: !canEdit, autosaves: canEdit }, flush: flushBeforeLeave }); // a failed save keeps the user here (Retry)

  // ------------------------------------------------------------------ keyboard
  const [keyOverrides, setKeyOverrides] = useState<KeyOverrides>(() => loadOverrides());
  const bindings = useMemo(() => resolveBindings(keyOverrides), [keyOverrides]);
  const keyOf = (id: string) => {
    const k = bindings.get(id)?.[0];
    return k ? formatCombo(k, mac) : null;
  };
  const [sheetKeys, setSheetKeys] = useState(false);
  const generateRef = useRef<(() => void) | null>(null);
  const sheetNav = useRef<((dir: 1 | -1) => void) | null>(null);
  const sldKeys = useRef<{ rename: () => void; reset: () => void } | null>(null);
  const problems = useMemo(() => problemIds(checks), [checks]);
  const run = (id: string) => {
    const steps: StepId[] = ["equipment", "site", "structure", "wiring", "review"];
    const m = /^permit\.step(\d)$/.exec(id);
    if (m) return goStep(steps[Number(m[1]) - 1]!);
    switch (id) {
      case "mode.design":
      case "mode.sketch":
      case "mode.permit": {
        const to = workspaceForAction(id, "permit");
        return to && workspace.go(to);
      }
      case "help.sheet":
        return setSheetKeys((o) => !o);
      case "permit.nextProblem":
      case "permit.prevProblem": {
        const next = nextProblem(problems, selected, id === "permit.nextProblem" ? 1 : -1);
        if (next) locate(next);
        return;
      }
      case "permit.locate":
        return selected && locate(selected);
      case "permit.fix": {
        const c = checks.find((x) => x.id === selected);
        const fix = c && fixFor(c);
        return fix ? fix.apply() : toast(c ? t("permit.workspace.toast.noFix") : t("permit.workspace.toast.selectFirst", { key: keyOf("permit.nextProblem") ?? "N" }));
      }
      case "permit.checks":
        return phone ? setSheetOpen((o) => !o) : setPanelOpen((o) => !o);
      case "permit.generate":
        goStep("review");
        window.setTimeout(() => generateRef.current?.(), 60);
        return;
      case "permit.sheetPrev":
        return sheetNav.current?.(-1);
      case "permit.sheetNext":
        return sheetNav.current?.(1);
      case "permit.rename":
        return sldKeys.current?.rename();
      case "permit.resetElement":
        return sldKeys.current?.reset();
    }
  };
  const runRef = useRef(run);
  runRef.current = run;
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;
  useEffect(() => {
    const handled = (id: string) => id.startsWith("permit.") || id.startsWith("mode.") || id === "help.sheet";
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const typing = isTypingTarget(e.target as HTMLElement | null);
      const action = matchAction(e, bindingsRef.current, { scope: "permit", mac, typing, repeat: e.repeat });
      if (!action || !handled(action.id)) return;
      e.preventDefault();
      runRef.current(action.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mac]);

  // a new step starts at its top, and the stepper shows it (it scrolls on a phone)
  useEffect(() => {
    document.querySelector(".pw-content")?.scrollTo({ top: 0 });
    document.querySelector(".pw-step.on")?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [step]);

  // ------------------------------------------------------------------ the page
  const ws: Ws = {
    designId,
    version,
    doc,
    units,
    canEdit,
    phone,
    inputs,
    setInputs,
    body,
    preview,
    checks,
    notes,
    statuses,
    site: site.data,
    details: details ?? site.data?.details ?? EMPTY_DETAILS,
    setDetails,
    siteSave: siteSave.state,
    refreshSite,
    sld: sld.data,
    overrides,
    dispatch: dispatchRaw,
    sldSave: sldSave.state,
    rendered,
    planSets,
    focus,
    selected,
    setSelected,
    locate,
    focusAnchor,
    fixFor,
    credits: spendable,
    goStep,
    flushAll,
    flushBeforeLeave,
    returnTo: permitPath(designId, "review"),
    purchase: purchasePromptValue,
    dismissPurchase: () => setPurchase(null),
    generateRef,
    sheetNav,
    sldKeys,
  };
  const total = statuses.review;
  const cost = preview.data?.cost_lux ?? planSets.data?.cost_lux;
  const address = doc.address || doc.design_name || t("permit.workspace.designFallback");

  return (
    <div className={cx("pw-shell", !panelOpen && "panel-closed")}>
      <header className="pw-top">
        <Link to={`/designs/${designId}`} className="editor-back" aria-label={t("permit.workspace.backToDesign")}>
          <DirIcon><ArrowLeft size={18} /></DirIcon>
        </Link>
        <div className="pw-title">
          <b className="ellipsis">{address}</b>
          <span className="muted small ellipsis">
            {t(canEdit ? "permit.workspace.subtitle" : "permit.workspace.subtitleViewOnly", { version: shownVersion(version, doc.version_offset) })}
          </span>
        </div>
        <WorkspaceSwitch current="permit" onSwitch={workspace.go} shortcuts={{ design: keyOf("mode.design"), sketch: keyOf("mode.sketch"), permit: keyOf("mode.permit") }} />
        <div className="pw-top-actions">
          {canEdit && <WorkspaceSaveState state={saveState} onRetry={() => void flushAll()} />}
          {cost != null && (
            <span className="pw-credit-chip" title={cost > 0 ? t("permit.workspace.credit.paidHint") : t("permit.workspace.credit.freeHint")}>
              {cost > 0 ? (
                <>
                  <LuxGlyph size={14} style={{ verticalAlign: -2 }} /> <FormattedMessage id="permit.workspace.credit.planSet" values={{ b: (c) => <b>{c}</b>, cost: luxText(t, cost) }} />
                  {ws.credits != null && <span className="muted"> {t("permit.workspace.credit.available", { lux: Math.max(ws.credits, 0) / 100 })}</span>}
                </>
              ) : (
                <>
                  <CheckCircle2 size={14} /> {t("permit.workspace.credit.free")}
                </>
              )}
            </span>
          )}
          <button className="icon-btn hide-phone" onClick={() => setSheetKeys(true)} title={t("permit.workspace.keys.title", { key: keyOf("help.sheet") ?? "?" })} aria-label={t("permit.workspace.keys.label")}>
            <Keyboard size={18} />
          </button>
          <HelpLink article="permits/generate-a-plan-set/" label={t("permit.workspace.help")} className="icon-btn hide-phone" size={18} />
        </div>
      </header>

      <nav className="pw-stepper" aria-label={t("permit.workspace.stepper.label")}>
        {STEPS.map((s, i) => {
          const st = statuses[s.id];
          return (
            <button key={s.id} className={cx("pw-step", s.id === step && "on", `st-${st.state}`)} onClick={() => goStep(s.id)} aria-current={s.id === step ? "step" : undefined} title={t("permit.workspace.stepper.stepTitle", { label: t(s.label), key: keyOf(`permit.step${i + 1}`) ?? "" })}>
              <span className="pw-step-num">{i + 1}</span>
              <span className="pw-step-text">
                <span className="pw-step-label">{t(phone ? s.short : s.label)}</span>
                <span className="pw-step-badge">
                  {s.id === "review" ? (total.problems || total.state === "unchecked" ? stepBadge(t, total) : t("permit.workspace.stepper.ready")) : stepBadge(t, st)}
                </span>
              </span>
              {s.id !== "review" && STEP_ICON[st.state]}
              {i < STEPS.length - 1 && <DirIcon><ChevronRight size={14} className="pw-step-sep" aria-hidden /></DirIcon>}
            </button>
          );
        })}
        <div className="pw-stepper-end hide-phone">
          <span className="muted small">
            <FormattedMessage id="permit.workspace.stepper.hint" values={{ kbd: (c) => <kbd>{c}</kbd>, next: keyOf("permit.nextProblem") ?? "N" }} />
          </span>
        </div>
      </nav>

      {!canEdit && (
        <div className="pw-readonly" role="status">
          {t("permit.generate.viewOnly")}
        </div>
      )}

      <div className="pw-main">
        <main className="pw-content" aria-live="polite">
          {preview.error && !preview.data ? (
            <div className="pw-pad">
              <Alert tone="danger" title={t("permit.workspace.preview.failed")}>
                {errorMessage(preview.error)}{" "}
                <button className="link-btn" onClick={() => preview.refetch()}>{t("common.retry")}</button>
              </Alert>
            </div>
          ) : preview.data?.enabled === false ? (
            <div className="pw-pad">
              <Alert tone="warn" title={t("permit.workspace.preview.offTitle")}>{t("permit.workspace.preview.offBody")}</Alert>
            </div>
          ) : null}
          {step === "equipment" && <EquipmentStep ws={ws} />}
          {step === "site" && <SiteStep ws={ws} />}
          {step === "structure" && <StructureStep ws={ws} />}
          {step === "wiring" && <WiringStep ws={ws} />}
          {step === "review" && <ReviewStep ws={ws} />}
          {step !== "review" && (
            <div className="pw-next">
              <Button
                variant="primary"
                onClick={() => {
                  const i = STEPS.findIndex((s) => s.id === step);
                  goStep(STEPS[i + 1]!.id);
                  document.querySelector(".pw-content")?.scrollTo({ top: 0 });
                }}
              >
                {t("permit.workspace.next", { step: t(STEPS[STEPS.findIndex((s) => s.id === step) + 1]!.label) })} <DirIcon><ChevronRight size={16} /></DirIcon>
              </Button>
            </div>
          )}
        </main>
        {!phone && (
          <aside className={cx("pw-checks-panel", !panelOpen && "closed")} aria-label={t("permit.checks.title")}>
            {panelOpen ? (
              <ChecksPanel ws={ws} onClose={() => setPanelOpen(false)} toggleKey={keyOf("permit.checks")} />
            ) : (
              <button className="pw-panel-open" onClick={() => setPanelOpen(true)} title={t("permit.checks.show", { key: keyOf("permit.checks") ?? "\\" })}>
                <DirIcon><PanelRightOpen size={18} /></DirIcon>
                <span className="pw-vert">{t("permit.checks.title")}</span>
                {total.problems > 0 && <span className="pw-count fail">{total.problems}</span>}
              </button>
            )}
          </aside>
        )}
      </div>

      {phone && (
        <>
          <button className="pw-sheet-chip" onClick={() => setSheetOpen(true)}>
            <ListChecks size={16} /> {t("permit.checks.title")}
            {total.problems > 0 ? <span className="pw-count fail">{problemsText(t, total.problems)}</span> : <span className="pw-count ok">{t("permit.checks.okCount", { count: total.ok })}</span>}
          </button>
          {sheetOpen && (
            <div className="pw-sheet-backdrop" onClick={() => setSheetOpen(false)} role="presentation">
              <div className="pw-sheet" role="dialog" aria-label={t("permit.checks.title")} onClick={(e) => e.stopPropagation()}>
                <div className="pw-sheet-grip" aria-hidden />
                <ChecksPanel ws={ws} onClose={() => setSheetOpen(false)} toggleKey={null} />
              </div>
            </div>
          )}
        </>
      )}
      {sheetKeys && (
        <CheatSheet
          mac={mac}
          overrides={keyOverrides}
          groups={["Permit", "Help"]}
          onOverrides={(o) => {
            setKeyOverrides(o);
            saveOverrides(o);
          }}
          onClose={() => setSheetKeys(false)}
        />
      )}
    </div>
  );
}

/** Design | Permit, in the editor's and the workspace's top bars. */
// ------------------------------------------------------------------------------------------- checks panel
function ChecksPanel({ ws, onClose, toggleKey }: { ws: Ws; onClose: () => void; toggleKey: string | null }) {
  const t = useT();
  const [open, setOpen] = useState<Record<string, boolean>>({ gap: true, problem: true, pass: false });
  const groups = groupChecks(ws.checks);
  const total = ws.statuses.review;
  const listRef = useRef<HTMLDivElement>(null);
  const orphans = ws.sld?.orphaned ?? [];
  useEffect(() => {
    if (!ws.selected) return;
    const el = listRef.current?.querySelector(`[data-check="${CSS.escape(ws.selected)}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [ws.selected]);
  const loading = ws.preview.isLoading;
  return (
    <div className="pw-checks">
      <div className="pw-checks-head">
        <b>{t("permit.checks.title")}</b>
        <span className="pw-checks-counts">
          {total.gaps > 0 && <span className="pw-count fail" title={t("permit.checks.missingTitle")}>{t("permit.checks.missing", { count: total.gaps })}</span>}
          {total.problems - total.gaps > 0 && <span className="pw-count fail" title={t("permit.group.problem")}>{problemsText(t, total.problems - total.gaps)}</span>}
          <span className="pw-count ok" title={t("permit.group.pass")}>{t("permit.checks.okCount", { count: total.ok })}</span>
        </span>
        <button className="icon-btn" onClick={onClose} aria-label={t("permit.checks.hide")} title={toggleKey ? t("permit.checks.hideKey", { key: toggleKey }) : t("common.close")}>
          {toggleKey ? <DirIcon><PanelRightClose size={17} /></DirIcon> : <X size={18} />}
        </button>
      </div>
      {ws.preview.isFetching && !loading && <div className="pw-updating">{t("permit.checks.updating")}</div>}
      <div className={cx("pw-checks-list", ws.preview.isFetching && !loading && "stale")} ref={listRef}>
        {loading && (
          <div className="pw-skeleton" aria-busy>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} />
            ))}
          </div>
        )}
        {orphans.length > 0 && (
          <div className="pw-orphan-note">
            <Info size={14} className="pw-ic muted" />
            <span>
              {t("permit.checks.orphans", { count: orphans.length })}{" "}
              <button className="link-btn" onClick={() => ws.focusAnchor("wiring", "orphans")}>{t("permit.checks.showOrphans")}</button>
            </span>
          </div>
        )}
        {!loading && !ws.preview.error && ws.checks.length > 0 && !groups.some((g) => g.id !== "pass" && g.items.length) && (
          <p className="pw-no-problems small">
            <CheckCircle2 size={14} className="pw-ic ok" /> {t("permit.checks.noProblems")}
          </p>
        )}
        {groups.map((g) => {
          if (!g.items.length) return null;
          const meta = GROUPS.find((x) => x.id === g.id)!;
          const isOpen = open[g.id];
          const isProblem = g.id !== "pass";
          return (
            <section key={g.id} className={cx("pw-group", isProblem ? "fail" : "ok")}>
              <button className="pw-group-head" onClick={() => setOpen((o) => ({ ...o, [g.id]: !o[g.id] }))} aria-expanded={isOpen}>
                {isOpen ? <ChevronDown size={14} /> : <DirIcon><ChevronRight size={14} /></DirIcon>}
                {CHECK_ICON[g.id]}
                <b>{t(meta.label)}</b>
                <span className="muted">({g.items.length})</span>
                {meta.hint && <span className="pw-group-hint">{t(meta.hint)}</span>}
              </button>
              {isOpen && (
                <ul>
                  {g.items.map((c) => {
                    const sel = ws.selected === c.id;
                    const fix = ws.fixFor(c);
                    return (
                      <li key={c.id} data-check={c.id} className={cx("pw-check", sel && "sel")}>
                        <button className="pw-check-main" onClick={() => ws.setSelected(sel ? null : c.id)} aria-expanded={sel}>
                          <span className="pw-check-title" dir="auto">{c.title}</span>
                          {c.reference && <span className="pw-check-ref">{c.reference}</span>}
                          {!sel && !isProblem && <span className="pw-check-sub" dir="auto">{c.detail}</span>}
                        </button>
                        {(sel || isProblem) && <p className="pw-check-detail" dir="auto">{c.detail}</p>}
                        {(sel || isProblem) && (
                          <div className="pw-check-actions">
                            <button className="pw-mini" onClick={() => ws.locate(c.id)}>
                              <Crosshair size={13} /> {t("permit.checks.locate")}
                            </button>
                            {fix && (
                              <button className="pw-mini fix" onClick={fix.apply}>
                                <Wrench size={13} /> {fix.label}
                              </button>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
        {!loading && ws.checks.length === 0 && !ws.preview.error && <p className="muted small pw-pad">{t("permit.checks.none")}</p>}
      </div>
      <div className="pw-checks-foot muted small">
        <MapIcon size={13} /> {t("permit.checks.footer")}
      </div>
    </div>
  );
}

/** Scrolls to and flashes `[data-anchor=…]` when a check is located. */
export function useFocusAnchor(focus: Focus | null, onAnchor?: (anchor: string) => void) {
  const onRef = useRef(onAnchor);
  onRef.current = onAnchor;
  useEffect(() => {
    if (!focus) return;
    onRef.current?.(focus.anchor);
    const t = window.setTimeout(() => {
      const el = document.querySelector(`[data-anchor="${CSS.escape(focus.anchor)}"]`) ?? document.querySelector(`[data-anchor="${CSS.escape(focus.anchor.split(":")[0]!)}"]`);
      if (!el) return;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.classList.remove("pw-flash");
      void (el as HTMLElement).offsetWidth;
      el.classList.add("pw-flash");
    }, 80);
    return () => window.clearTimeout(t);
  }, [focus]);
}

/** A step's heading with its status and the autosave indicator. */
export function StepHead({ title, description, status, save, children }: { title: string; description: ReactNode; status?: StepStatus; save?: SaveState; children?: ReactNode }) {
  return (
    <div className="pw-step-head">
      <div>
        <h1>
          {title} {status && <StatusBadge s={status} />}
        </h1>
        <p className="muted">{description}</p>
      </div>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        {save && <SaveChip state={save} />}
        {children}
      </div>
    </div>
  );
}

export function StatusBadge({ s }: { s: StepStatus }) {
  const t = useT();
  const tone = s.state === "problem" ? "danger" : s.state === "notstarted" ? undefined : "ok";
  return <Badge tone={tone}>{stepBadge(t, s)}</Badge>;
}

/** The workspace's one save indicator (draft, site details, SLD edits), like the Sketch editor's. */
function saveStateText(t: Translate, state: SaveState): string {
  return state === "saved" ? t("permit.workspace.save.saved") : state === "saving" ? t("common.saving") : state === "pending" ? t("permit.workspace.save.pending") : t("permit.workspace.save.error");
}

function WorkspaceSaveState({ state, onRetry }: { state: SaveState; onRetry: () => void }) {
  const t = useT();
  const icon = state === "saving" ? <Loader2 size={14} className="pw-spin" /> : state === "error" ? <AlertTriangle size={14} /> : state === "saved" ? <CheckCircle2 size={14} /> : null;
  return (
    <span className={cx("pw-saved", state)} role="status" aria-live="polite" title={state === "saved" ? t("permit.workspace.save.savedHint") : undefined}>
      {icon} {saveStateText(t, state)}
      {state === "error" && (
        <button className="link-btn" onClick={onRetry}>
          {t("permit.workspace.save.retry")}
        </button>
      )}
    </span>
  );
}

export function SaveChip({ state }: { state: SaveState }) {
  const t = useT();
  const text = state === "saved" ? t("common.saved") : state === "saving" ? t("common.saving") : state === "pending" ? t("permit.workspace.save.pending") : t("permit.workspace.save.errorRetrying");
  return <span className={cx("pw-save", state)} role="status">{text}</span>;
}

/** The step's problems next to its form (with their fix), then its notes as plain information. */
export function StepChecks({ ws, step }: { ws: Ws; step: WorkStep }) {
  const mine = ws.checks.filter((c) => locateCheck(c.id).step === step && c.status === "problem");
  const notes = ws.notes.filter((c) => locateCheck(c.id).step === step);
  if (!mine.length && !notes.length) return null;
  return (
    <>
      {mine.length > 0 && (
        <ul className="pw-step-checks">
          {mine.map((c) => {
            const fix = ws.fixFor(c);
            return (
              <li key={c.id} className={cx("pw-step-check", "fail", ws.selected === c.id && "sel")}>
                {CHECK_ICON.problem}
                <div>
                  <b dir="auto">{c.title}</b>
                  {c.reference && <span className="muted small"> · {c.reference}</span>}
                  <div className="small" dir="auto">{c.detail}</div>
                  {fix && (
                    <button className="pw-mini fix" onClick={fix.apply}>
                      <Wrench size={13} /> {fix.label}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {notes.length > 0 && (
        <ul className="pw-step-notes">
          {notes.map((n) => (
            <li key={n.id}>
              <Info size={14} className="pw-ic muted" />
              <span>
                <b>{n.title.replace(/^Structural: (.)/, (_m, c: string) => c.toUpperCase())}</b> {n.detail}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Generates the plan set (the Review step's card); exported for the summary's tests of labels. */
export function useGenerate(ws: Ws) {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const lux = useLuxSpend();
  return useMutation({
    mutationFn: (opts?: { allowIncomplete?: boolean }) =>
      unwrap(
        api.POST("/v1/designs/{design_id}/versions/{version_number}/plan-set", {
          params: {
            path: { design_id: ws.designId, version_number: ws.version },
            query: opts?.allowIncomplete ? { allow_incomplete: true } : {},
            header: { "Idempotency-Key": crypto.randomUUID() },
          },
          body: ws.body,
        }),
      ),
    onSuccess: (p) => {
      const known = ws.planSets.data?.items.some((x) => x.id === p.id);
      qc.invalidateQueries({ queryKey: ["plan-sets", ws.designId] });
      if (known) return toast(t("permit.workspace.toast.unchanged", { number: p.number }));
      qc.invalidateQueries({ queryKey: ["lux"] });
      qc.invalidateQueries({ queryKey: ["plan-set-preview", ws.designId] });
      if (p.lux) lux.spent({ amount: p.lux, label: t("permit.workspace.lux.planSetNumber", { number: p.number }), left: ws.credits != null ? ws.credits - p.lux : null, href: activityHref("plan_set") });
      toast(
        p.status === "ready"
          ? t("permit.workspace.toast.ready", { number: p.number })
          : p.lux
            ? t("permit.workspace.toast.orderedPaid", { number: p.number, openItems: p.open_items ?? 0, cost: luxText(t, p.lux) })
            : t("permit.workspace.toast.orderedFree", { number: p.number, openItems: p.open_items ?? 0 }),
      );
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 402) return; // the caller shows the out-of-Lux popover (Buy Lux)
      if (e instanceof ApiError && e.status === 409) {
        qc.invalidateQueries({ queryKey: ["plan-set-preview", ws.designId] }); // the gaps changed: show them again
        return toast(t("permit.workspace.toast.noLuxUsed", { message: e.message }), true);
      }
      toast(t("permit.workspace.toast.noLuxUsed", { message: errorMessage(e) }), true);
    },
  });
}

/** Generate, from the Review card or the post-purchase prompt: asks first (cost, Lux available); without enough Lux
 * (or when the server says so: 402) the popover offers Buy Lux, which saves the work and comes back here. */
export function useStartGenerate(ws: Ws) {
  const generate = useGenerate(ws);
  const lux = useLuxSpend();
  const t = useT();
  const start = async (anchor: Element | null, cost: number, allowIncomplete = false) => {
    const req = { cost, label: t("permit.workspace.lux.planSet"), balance: ws.credits ?? null, returnTo: ws.returnTo, beforeLeave: ws.flushBeforeLeave };
    if (!(await lux.confirm(req, anchor))) return;
    await ws.flushAll(); // the plan set copies the saved site details and SLD edits
    generate.mutate({ allowIncomplete }, {
      onSuccess: () => ws.dismissPurchase(),
      onError: (e) => {
        if (e instanceof ApiError && e.status === 402) void lux.confirm({ ...req, canPay: false }, anchor);
      },
    });
  };
  return { start, generate };
}
